import React from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Badge as BadgeUI, Input } from "reactstrap";
import NotificationAlert from "react-notification-alert";
import api from "./../../../Api.js";
import SimpleHeader from "./../../../components/Headers/SimpleHeader.jsx";
import GoldDisplay from "./../rpg/GoldDisplay.jsx";
import { getLpcPreviewStyle } from "./../../../helper/lpcHelper.js";
import { recolorSpritesheet, recolorAllAnimations } from "./../../../helper/aiRecolor.js";

/**
 * AiAtelier — o "Estilista IA" ✨
 * O aluno descreve uma peça de roupa em linguagem natural, a IA gera o design
 * (paleta + padrão sobre um sprite base LPC), o front recoloriza o spritesheet
 * via canvas, mostra o preview e salva cobrando gold.
 */

var SLOT_LABELS = {
  torso: 'Roupa/Armadura',
  legs: 'Calça',
  feet: 'Calçado',
  top: 'Cabelo',
  helmet: 'Elmo',
  shield: 'Escudo'
};

var RARITY_LABELS = { rare: 'Rara', epic: 'Épica', legendary: 'Lendária' };

class AiAtelier extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // opções vindas do backend
      slots: ['torso', 'legs', 'feet', 'top', 'helmet', 'shield'],
      costByRarity: null,
      examples: [],
      // saldo
      balance: 0,
      // form
      prompt: '',
      slot: 'torso',
      // geração
      generating: false,
      spec: null,
      previewUrl: null,
      promptUsed: '',
      slotUsed: '',
      customName: '',
      // salvamento
      saving: false,
      savingStep: null,
      savedInfo: null,
      // minhas criações
      myItems: [],
      loadingMine: true
    };
  }

  async componentDidMount() {
    await Promise.all([this.loadOptions(), this.loadBalance(), this.loadMine()]);
  }

  async loadOptions() {
    try {
      var res = await api.aiClothesOptions();
      var data = res.data || {};
      this.setState({
        slots: (Array.isArray(data.slots) && data.slots.length > 0) ? data.slots : this.state.slots,
        costByRarity: data.costByRarity || null,
        examples: Array.isArray(data.examples) ? data.examples : []
      });
    } catch (e) {
      this.notify('danger', 'Ops!', 'Não consegui carregar as opções do ateliê 😢');
    }
  }

  async loadBalance() {
    if (!this.props.user || !this.props.user.id) return;
    try {
      var res = await api.getRpgStatus(this.props.user.id);
      this.setState({ balance: (res.data && res.data.gold && res.data.gold.balance) || 0 });
    } catch (e) {
      this.notify('danger', 'Ops!', 'Não consegui carregar seu saldo de gold 😢');
    }
  }

  async loadMine() {
    try {
      var res = await api.aiClothesMine();
      this.setState({ myItems: Array.isArray(res.data) ? res.data : [], loadingMine: false });
    } catch (e) {
      this.setState({ loadingMine: false });
      this.notify('danger', 'Ops!', 'Não consegui carregar suas criações 😢');
    }
  }

  notify(type, title, message) {
    try {
      this.refs.notificationAlert.notificationAlert({
        place: 'tr',
        message: <div className="alert-text"><span className="alert-title">{title}</span><span> {message}</span></div>,
        type: type, icon: 'ni ni-bell-55', autoDismiss: 5
      });
    } catch (e) {}
  }

  async generate() {
    var prompt = this.state.prompt.trim();
    if (!prompt) {
      this.notify('warning', 'Calma aí!', 'Descreva sua peça antes de gerar ✍️');
      return;
    }
    if (this.state.generating) return;

    this.setState({ generating: true, spec: null, previewUrl: null, savedInfo: null });

    var spec = null;
    try {
      var res = await api.aiClothesDesign({ prompt: prompt, slot: this.state.slot });
      spec = res.data;
    } catch (e) {
      var msg = (e.response && e.response.data && e.response.data.error) || 'A IA não conseguiu gerar o design. Tente de novo!';
      this.setState({ generating: false });
      this.notify('danger', 'Ops!', msg);
      return;
    }

    try {
      var previewUrl = await recolorSpritesheet(spec.baseLayer, spec.palette, spec.pattern);
      this.setState({
        generating: false,
        spec: spec,
        previewUrl: previewUrl,
        promptUsed: prompt,
        slotUsed: this.state.slot,
        customName: spec.name || ''
      });
      // saldo pode ter mudado desde o mount
      if (typeof spec.balance === 'number') this.setState({ balance: spec.balance });
    } catch (e) {
      this.setState({ generating: false });
      this.notify('danger', 'Ops!', e.message || 'Falha ao montar o preview do sprite 😢');
    }
  }

  async save() {
    var spec = this.state.spec;
    if (!spec || !this.state.previewUrl || this.state.saving) return;
    if (!spec.canAfford) {
      this.notify('warning', 'Saldo insuficiente', 'Você precisa de ' + spec.cost + ' gold pra criar essa peça 🪙');
      return;
    }

    var name = this.state.customName.trim() || spec.name;
    this.setState({ saving: true });

    try {
      //Gera a peca em todas as animacoes do personagem. Sem isso ela ficaria
      //presa na pose de caminhada e sairia do lugar quando o heroi ataca.
      var todas = await recolorAllAnimations(
        spec.baseLayer, spec.palette, spec.pattern,
        (anim, feitas, total) => this.setState({ savingStep: 'Costurando ' + anim + ' (' + feitas + '/' + total + ')' })
      );
      var images = todas.images;
      if (!images.walk) images.walk = this.state.previewUrl;

      var res = await api.aiClothesSave({
        prompt: this.state.promptUsed,
        slot: this.state.slotUsed,
        images: images,
        imageBase64: images.walk,
        name: name
      });
      var data = res.data || {};
      this.setState({
        saving: false,
        savingStep: null,
        savedInfo: data,
        balance: (typeof data.balanceAfter === 'number') ? data.balanceAfter : this.state.balance
      });
      this.notify('success', '🎉 ' + ((data.item && data.item.name) || name), data.message || 'Peça criada com sucesso!');
      await this.loadMine();
    } catch (e) {
      this.setState({ saving: false, savingStep: null });
      var errData = (e.response && e.response.data) || {};
      if (e.response && e.response.status === 402) {
        if (typeof errData.balance === 'number') this.setState({ balance: errData.balance });
        this.notify('warning', 'Saldo insuficiente 🪙', errData.error || ('Faltam ' + (errData.needed || '?') + ' gold pra essa criação.'));
      } else {
        this.notify('danger', 'Ops!', errData.error || 'Não consegui salvar sua peça 😢');
      }
    }
  }

  rarityColor(r) {
    var c = { rare: '#2196F3', epic: '#9C27B0', legendary: '#FF9800' };
    return c[r] || '#9E9E9E';
  }

  renderCostInfo() {
    var cost = this.state.costByRarity;
    if (!cost) return null;
    return (
      <div className="mt-3" style={{ fontSize: '0.8rem', color: '#8898aa' }}>
        <i className="fas fa-coins mr-1" style={{ color: '#FFA500' }} />
        Custo por raridade:
        <BadgeUI pill className="ml-2" style={{ background: this.rarityColor('rare'), color: '#fff' }}>Rara {cost.rare}g</BadgeUI>
        <BadgeUI pill className="ml-1" style={{ background: this.rarityColor('epic'), color: '#fff' }}>Épica {cost.epic}g</BadgeUI>
        <BadgeUI pill className="ml-1" style={{ background: this.rarityColor('legendary'), color: '#fff' }}>Lendária {cost.legendary}g</BadgeUI>
      </div>
    );
  }

  renderPreview() {
    var spec = this.state.spec;

    if (this.state.generating) {
      return (
        <div className="text-center py-5">
          <i className="fas fa-magic fa-spin fa-2x" style={{ color: '#9C27B0' }} />
          <p className="mt-3 text-muted">A IA está costurando sua peça... 🧵✨</p>
        </div>
      );
    }

    if (!spec || !this.state.previewUrl) {
      return (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-tshirt fa-3x mb-3" style={{ opacity: 0.3 }} />
          <p>Descreva sua peça ao lado e clique em <strong>Gerar Design ✨</strong> pra ver a mágica acontecer!</p>
        </div>
      );
    }

    var color = this.rarityColor(spec.rarity);
    var palette = spec.palette || {};
    var saved = this.state.savedInfo;

    return (
      <div className="text-center">
        {/* Frame "south" do spritesheet (crop 64x64 em x=0, y=128) escalado 3x */}
        <div style={{
          width: '192px', height: '192px', margin: '0 auto',
          background: 'linear-gradient(180deg, #b3d9ff, #87ceeb)',
          borderRadius: '12px', overflow: 'hidden', position: 'relative',
          border: '3px solid ' + color
        }}>
          <div style={{
            width: '64px', height: '64px',
            backgroundImage: 'url(' + this.state.previewUrl + ')',
            backgroundPosition: '0px -128px',
            backgroundRepeat: 'no-repeat',
            imageRendering: 'pixelated',
            transform: 'scale(3)',
            transformOrigin: '0 0',
            position: 'absolute', top: 0, left: 0
          }} />
        </div>

        <h4 className="mt-3 mb-1">{spec.name}</h4>
        <BadgeUI pill style={{ background: color, color: '#fff' }}>
          {RARITY_LABELS[spec.rarity] || spec.rarity}
        </BadgeUI>
        <span className="ml-2" style={{ fontWeight: 700, color: '#FFA500' }}>
          <i className="fas fa-coins mr-1" />{spec.cost}g
        </span>

        {/* Paleta gerada */}
        <div className="mt-2" style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
          {[palette.primary, palette.secondary, palette.accent].map(function (c, idx) {
            return (
              <span key={idx} title={c} style={{
                width: '22px', height: '22px', borderRadius: '50%',
                background: c || '#ccc', display: 'inline-block',
                border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} />
            );
          })}
        </div>

        {/* Nome customizável */}
        <div className="mt-3 text-left">
          <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#525f7f' }}>Nome da peça (opcional)</label>
          <Input
            type="text"
            maxLength={40}
            value={this.state.customName}
            onChange={(e) => this.setState({ customName: e.target.value })}
            placeholder={spec.name}
            disabled={!!saved}
          />
        </div>

        {!spec.canAfford && !saved && (
          <div className="mt-3 p-2" style={{ background: '#fff3cd', borderRadius: '8px', color: '#856404', fontSize: '0.85rem' }}>
            <i className="fas fa-exclamation-triangle mr-1" />
            Saldo insuficiente! Você tem {this.state.balance}g e precisa de {spec.cost}g. Ganhe gold nas missões e aulas! ⚔️
          </div>
        )}

        {saved ? (
          <div className="mt-3 p-3" style={{ background: '#d4edda', borderRadius: '8px', color: '#155724' }}>
            <i className="fas fa-check-circle mr-1" />
            {saved.equipped ? 'Peça criada e já equipada no seu herói! 🎉' : 'Peça criada e guardada no seu inventário! 🎉'}
            <div className="mt-2">
              <Link to="/game/personagem" className="btn btn-success btn-sm">
                <i className="fas fa-dragon mr-1" /> Ver no personagem
              </Link>
            </div>
          </div>
        ) : (
          <Button
            color="warning"
            block
            className="mt-3"
            onClick={() => this.save()}
            disabled={!spec.canAfford || this.state.saving}
          >
            {this.state.saving
              ? <span><i className="fas fa-spinner fa-spin mr-1" /> {this.state.savingStep || 'Costurando...'}</span>
              : <span>Criar por {spec.cost}g 🪙</span>}
          </Button>
        )}
      </div>
    );
  }

  renderMine() {
    if (this.state.loadingMine) {
      return <div className="text-center py-4"><i className="fas fa-spinner fa-spin fa-2x text-muted" /></div>;
    }
    if (this.state.myItems.length === 0) {
      return (
        <div className="text-center py-4 text-muted">
          <i className="fas fa-box-open fa-2x mb-2" style={{ opacity: 0.3 }} />
          <p className="mb-0">Você ainda não criou nenhuma peça. Solta a imaginação aí em cima! 🎨</p>
        </div>
      );
    }

    return (
      <Row>
        {this.state.myItems.map((item) => {
          var color = this.rarityColor(item.rarity);
          return (
            <Col xs="6" sm="4" md="3" lg="2" key={item.id} className="mb-3">
              <div style={{
                borderRadius: '10px', padding: '10px', background: '#fff',
                border: '2px solid ' + color + '60', textAlign: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.05)', height: '100%'
              }}>
                <div style={{
                  width: '90px', height: '90px', margin: '0 auto',
                  background: 'linear-gradient(180deg, #b3d9ff, #87ceeb)',
                  borderRadius: '8px', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '2px solid ' + color
                }}>
                  {item.lpcLayer && <div style={getLpcPreviewStyle(item.lpcLayer, 80, item.slot)} />}
                </div>
                <h6 className="mt-2 mb-1" style={{ fontSize: '0.8rem' }}>{item.name}</h6>
                <BadgeUI pill style={{ background: color, color: '#fff', fontSize: '0.6rem' }}>
                  {RARITY_LABELS[item.rarity] || item.rarity}
                </BadgeUI>
                {item.aiPrompt && (
                  <p className="mt-1 mb-0" style={{ fontSize: '0.65rem', fontStyle: 'italic', color: '#8898aa' }}>
                    "{item.aiPrompt}"
                  </p>
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    );
  }

  render() {
    return (
      <React.Fragment>
        <div className="rna-wrapper"><NotificationAlert ref="notificationAlert" /></div>
        {!this.props.embedded && <SimpleHeader name="✨ Estilista IA" way={[{ name: "Estilista IA", url: "/ai-atelier" }]} />}

        <Container className={this.props.embedded ? "mt-3" : "mt--6"} fluid>
          {/* Header com saldo + dica */}
          <Card className="shadow mb-3" style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #e83e8c 100%)', color: '#fff', border: 'none' }}>
            <CardBody className="text-center">
              <h3 className="mb-1" style={{ color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                <i className="fas fa-magic mr-2" />
                Estilista IA
              </h3>
              <p className="mb-2" style={{ opacity: 0.9, fontSize: '0.9rem' }}>
                💡 Descreva cores, temas — fogo, gelo, galáxia, neon... — e padrões: listrado, bolinhas, xadrez, degradê. A IA desenha, você veste! 👕
              </p>
              <GoldDisplay balance={this.state.balance} />
            </CardBody>
          </Card>

          <Row>
            {/* Form */}
            <Col lg="6" className="mb-3">
              <Card className="shadow h-100">
                <CardHeader className="border-0">
                  <h5 className="mb-0"><i className="fas fa-pencil-ruler mr-2" style={{ color: '#6f42c1' }} />Descreva sua peça</h5>
                </CardHeader>
                <CardBody>
                  <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#525f7f' }}>O que você quer vestir?</label>
                  <Input
                    type="textarea"
                    rows="3"
                    maxLength={200}
                    value={this.state.prompt}
                    onChange={(e) => this.setState({ prompt: e.target.value })}
                    placeholder="Ex: armadura de dragão de fogo com detalhes dourados e listras"
                  />
                  <div className="text-right" style={{ fontSize: '0.7rem', color: '#8898aa' }}>
                    {this.state.prompt.length}/200
                  </div>

                  <label className="mt-2" style={{ fontSize: '0.8rem', fontWeight: 600, color: '#525f7f' }}>Tipo de peça</label>
                  <Input
                    type="select"
                    value={this.state.slot}
                    onChange={(e) => this.setState({ slot: e.target.value })}
                  >
                    {this.state.slots.map((s) => (
                      <option key={s} value={s}>{SLOT_LABELS[s] || s}</option>
                    ))}
                  </Input>

                  {this.state.examples.length > 0 && (
                    <div className="mt-3">
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#525f7f' }}>Precisa de inspiração? 💭</div>
                      <div className="mt-1">
                        {this.state.examples.map((ex, idx) => (
                          <BadgeUI
                            key={idx}
                            pill
                            className="mr-1 mb-1"
                            style={{ background: '#f6f0ff', color: '#6f42c1', cursor: 'pointer', fontSize: '0.7rem', border: '1px solid #6f42c150' }}
                            onClick={() => this.setState({ prompt: ex })}
                          >
                            {ex}
                          </BadgeUI>
                        ))}
                      </div>
                    </div>
                  )}

                  {this.renderCostInfo()}

                  <Button
                    color="primary"
                    block
                    className="mt-3"
                    style={{ background: 'linear-gradient(135deg, #6f42c1, #9c27b0)', border: 'none' }}
                    onClick={() => this.generate()}
                    disabled={this.state.generating || !this.state.prompt.trim()}
                  >
                    {this.state.generating
                      ? <span><i className="fas fa-spinner fa-spin mr-1" /> Gerando...</span>
                      : <span>Gerar Design ✨</span>}
                  </Button>
                </CardBody>
              </Card>
            </Col>

            {/* Preview */}
            <Col lg="6" className="mb-3">
              <Card className="shadow h-100">
                <CardHeader className="border-0">
                  <h5 className="mb-0"><i className="fas fa-eye mr-2" style={{ color: '#2196F3' }} />Preview</h5>
                </CardHeader>
                <CardBody>
                  {this.renderPreview()}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Minhas criações */}
          <Card className="shadow mb-4">
            <CardHeader className="border-0">
              <h5 className="mb-0"><i className="fas fa-tshirt mr-2" style={{ color: '#e83e8c' }} />Minhas Criações</h5>
            </CardHeader>
            <CardBody>
              {this.renderMine()}
            </CardBody>
          </Card>
        </Container>
      </React.Fragment>
    );
  }
}

export default connect(state => ({ user: state.user }))(AiAtelier);
